import { constants } from '../constants/constants';
import { isServer } from '../device_detection/device_detection';
import { handleEmptyObjects } from '../handleEmptyObjects/handleEmptyObjects';
import { STORAGE_KEY } from '../storageKeys/storageKeys';
import * as utils from './oneTrust';

/**
 * The contains the logic to render OneTrust scripts
 *
 * @module utils/oneTrust/oneTrust
 * @method
 * @param { Object } data - containing OneTrust script, domain and enable flag
 * @param { Object } methods - reserved methods object for extension
 * @returns string
**/
export const oneTrust = ( data ) => {
  const { script, domain, enabled, disableOneTrust } = data || {};

  if( enabled === 'true' && domain && script && !disableOneTrust ){
    return `
    <!-- OneTrust Cookies Consent Notice start -->
    ${ utils.dataSubjectParamsScript() }
    <script
      src="${ script }"
      data-document-language="true"
      type="text/javascript"
      charset="UTF-8"
      data-domain-script="${ domain }" >
    </script>
    <script type="text/javascript">
      window.__HAS_APPLIED_CONSENT__ = false;
      function OptanonWrapper() {
        if (window.__HAS_APPLIED_CONSENT__) {
          return; // Prevent re-application of consent
        }
        // Read tracking-authorized parameter from URL
        var urlParams = new URLSearchParams(window.location.search);
        var trackingParam = urlParams.get('tracking-authorized');
        var consentChoice = trackingParam === 'true' ? true : trackingParam === 'false' ? false : undefined;
        if (consentChoice === true) {
          window.__HAS_APPLIED_CONSENT__  = true;
          OneTrust.AllowAll();
          return;
        } else if (consentChoice === false) {
          window.__HAS_APPLIED_CONSENT__  = true;
          OneTrust.RejectAll();
          return;
        }
        if(window.__IS_NATIVE__){
          window.__HAS_APPLIED_CONSENT__ = true;
          // Fallback safeguard: native app should always pass the tracking-authorized param,
          // so consent is normally applied above and the OneTrust banner never renders.
          // If that param is missing (unexpected — e.g. deeplink bug), IsAlertBoxClosed() 
          // returns false because no consent cookie exists yet, meaning the banner would
          // be shown inside the webview. Reject to suppress it; on subsequent webviews
          // the cookie is set, the alert box is "closed", and we skip.
          if (!OneTrust.IsAlertBoxClosed()) {
            OneTrust.RejectAll();
          }
        }
      }
    </script>
    <!-- OneTrust Cookies Consent Notice end -->
    `;
  }
  else {
    return '';
  }
};

/**
 * Inline script rendered ahead of the OneTrust stub that declares a signed-in user's identity.
 * OneTrust expects `dataSubjectParams` before its script on every page after login, and none after
 * logout; otherwise the stub treats each page load as an identity change and clears the consent
 * cookies. The stored user is written by UserContextProvider on every session resolve and removed
 * on sign-out, and `otToken` is the DXL-minted OneTrust JWT whose `sub` is the GTI.
 *
 * @method
 * @returns {string} script tag
 */
export const dataSubjectParamsScript = () => `
    <script type="text/javascript">
      (function () {
        try {
          var user = JSON.parse(window.localStorage.getItem('${STORAGE_KEY.user}'));
          if (user && user.loginType === '${constants.LOGIN_STATUS.HARD_LOGIN}' && user.gti && user.otToken) {
            window.OneTrust = { dataSubjectParams: { id: user.gti, token: user.otToken, isAnonymous: false } };
          }
        } catch (e) {}
      })();
    </script>`.trim();

/**
 * Syncs a hard-logged-in user's consent profile with OneTrust and reloads the banner so it
 * reflects the synced preferences.
 *
 * The token is a JWT minted by DXL (`isOTRequired` module param on the post
 * auth0 session flow) whose `sub` is the user's GTI, which is also the
 * OneTrust cross-domain identifier.
 *
 * @method
 * @param {object} data - arguments
 * @param {object} data.user - user object
 * @returns {void}
 */
export const getSyncProfile = ( data ) => {
  const { user = {} } = handleEmptyObjects( data );
  const { gti, otToken, loginType } = user;

  const isHardLogin = loginType === constants.LOGIN_STATUS.HARD_LOGIN;

  if( !isHardLogin || !gti || !otToken || isServer() || !global.OneTrust?.syncConsentProfile ){
    return;
  }

  global.OneTrust.syncConsentProfile( gti, otToken, false );
};

/**
 * Group Ids and definitions from OneTrust
 * https://uat.onetrust.com/cookies/categorizations?tab=Categories
 * @type object
 * @property { string } Advertising - Advertising cookies are used to provide you with a personalized experience. These cookies may be set by our advertising partners to build a profile of your interests.
 * @property { string } Required - Required cookies are used when saving your settings and preferences, when you log in and out of your account, and for other basic site functions. These cookies cannot be disabled.
 * @property { string } Functional - These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages.
 * @property { string } Targeting - These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.
 * @property { string } SocialMedia - These cookies are set by a range of social media services that we have added to the site to enable you to share our content with your friends and networks. They are capable of tracking your browser across other sites and building up a profile of your interests. This may impact the content and messages you see on other websites you visit.
 * @property { string } Performance - These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.
 */
export const groupTypes = {
  'Advertising': 'C0001',
  'Required': 'C0002',
  'Functional': 'C0003',
  'Targeting': 'C0004',
  'SocialMedia': 'C0005',
  'Performance': 'C0007'
};

/**
 * Represents a OneTrust Group Validation method

 * @method
 * @param {object} data - React properties passed from composition
 * @returns boolean
 */
export const isValidOneTrustGroup = ( data ) => {
  const {
    groupName,
    groupTypes = utils.groupTypes
  } = data || {};
  return ( !groupName ) ? false : Object.keys( groupTypes ).includes( groupName );
};

/**
 * Represents a OneTrust Class
 * https://my.onetrust.com/s/article/UUID-730ad441-6c4d-7877-7f85-36f1e801e8ca
 * @method
 * @param {object} data - React properties passed from composition
 * @returns string
 */
export const setOneTrustClass = ( data, methods ) => {
  const {
    groupName,
    groupTypes = utils.groupTypes
  } = data || {};
  const {
    isValidOneTrustGroup = utils.isValidOneTrustGroup
  } = methods || {};
  if( isValidOneTrustGroup( data ) && groupTypes[ groupName ] !== groupTypes.Required ){
    return `optanon-category-${ groupTypes[ groupName ] }`;
  }
  else {
    return '';
  }
};

/**
 * Represents a OneTrust data type for script tags
 * https://my.onetrust.com/s/article/UUID-730ad441-6c4d-7877-7f85-36f1e801e8ca
 * @method
 * @param {object} data - React properties passed from composition
 * @returns string
 */
export const setOneTrustType = ( data, methods ) => {
  const {
    type,
    groupName,
    groupTypes = utils.groupTypes
  } = data || {};
  const {
    isValidOneTrustGroup = utils.isValidOneTrustGroup
  } = methods || {};

  if( isValidOneTrustGroup( data ) && groupTypes[ groupName ] !== groupTypes.Required ){
    return 'text/plain';
  }
  else if( type ){
    return type;
  }
  else {
    return '';
  }
};
